import { create } from "zustand";
import { message } from "antd";

import AuthApiCaller from "../config/api/authapicaller";
import { setUnauthorizedHandler } from "../config/api/client";
import { resetAllStores } from "./resetStores";

const readStoredUser = () => {
  const stored = localStorage.getItem("authUser");
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch (err) {
    return null;
  }
};

const buildInitialState = () => ({
  isLoggedIn: !!localStorage.getItem("authUser"),
  user: readStoredUser() || {},
  isLoading: false,
  isEmailLoading: false,
  isPasswordLoading: false,
  error: null,
});

export const useAuthStore = create((set) => {
  const setAuth = (partial) => set((state) => ({ ...state, ...partial }));

  return {
    ...buildInitialState(),
    signIn: async (payload, cb) => {
      try {
        setAuth({ isLoading: true, error: null });
        localStorage.setItem("authUser", JSON.stringify(payload));
        setAuth({
          user: { ...payload },
          isLoggedIn: true,
          isLoading: false,
        });
        if (cb) cb(payload);
      } catch (err) {
        setAuth({ isLoading: false, error: err });
      }
    },
    logout: async () => {
      try {
        setAuth({ isLoading: true, error: null });
        localStorage.removeItem("authUser");
        set(buildInitialState());
        resetAllStores();
      } catch (err) {
        setAuth({ isLoading: false, error: err });
      }
    },
    forgotPassword: async (payload, cb) => {
      try {
        setAuth({ isPasswordLoading: true, error: null });
        const res = await AuthApiCaller.Post("/auth/forgotpassword", payload);
        if (res?.data?.Data?.["$metadata"]?.httpStatusCode === 200) {
          message.success("OTP sent to email.");
          setAuth({ isPasswordLoading: false });
        } else if (
          res?.data?.Data?.["$metadata"]?.httpStatusCode === 400 &&
          res?.data?.Data?.name === "LimitExceededException"
        ) {
          message.error("Request limit exceeded.");
          setAuth({ isPasswordLoading: false });
        } else {
          message.error("Something went wrong");
          setAuth({ isPasswordLoading: false });
        }
        if (cb) cb(res?.data?.Data?.["$metadata"]?.httpStatusCode);
      } catch (err) {
        message.error("Something went wrong");
        setAuth({ isPasswordLoading: false, error: err });
      }
    },
    resendOtp: async (payload, cb) => {
      try {
        setAuth({ isLoading: true, error: null });
        const res = await AuthApiCaller.Post("/auth/resendotp", payload);
        if (res?.data?.["$metadata"]?.httpStatusCode === 200) {
          message.success("OTP resent to email.");
        }
        setAuth({ isLoading: false });
        if (cb) cb(res);
      } catch (err) {
        message.error("Something went wrong");
        setAuth({ isLoading: false, error: err });
      }
    },
    confirmPassword: async (payload, cb) => {
      try {
        setAuth({ isPasswordLoading: true, error: null });
        const res = await AuthApiCaller.Post("/auth/confirmpassword", payload);
        const status = res?.data?.Data?.["$metadata"]?.httpStatusCode;
        const name = res?.data?.Data?.name;
        if (status === 200) {
          message.success("Password changed successfully.");
          setAuth({ isPasswordLoading: false });
        } else if (status === 400 && name === "LimitExceededException") {
          message.error("Request limit exceeded.");
          setAuth({ isPasswordLoading: false });
        } else if (status === 400 && name === "InvalidParameterException") {
          message.error("Invalid parameter.");
          setAuth({ isPasswordLoading: false });
        } else if (status === 400 && name === "CodeMismatchException") {
          message.error("Invalid OTP.");
          setAuth({ isPasswordLoading: false });
        } else if (status === 400 && name === "InvalidPasswordException") {
          message.error("Password should be at least 6 characters.");
          setAuth({ isPasswordLoading: false });
        } else {
          message.error("Something went wrong");
          setAuth({ isPasswordLoading: false });
        }
        if (cb) cb(status);
      } catch (err) {
        message.error("Something went wrong");
        setAuth({ isPasswordLoading: false, error: err });
      }
    },
    reset: () => set(buildInitialState()),
  };
});

setUnauthorizedHandler(() => {
  const logout = useAuthStore.getState().logout;
  if (logout) logout();
});
